import { Card, CardBody, CardHeader, Flex, Text } from '@backstage/ui';
import type {
  CheckFilter,
  TrackDefinition,
} from '@internal/backstage-plugin-pulse-common';
import { TRACK_FILTER_FIELDS } from '../lib';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { LabelValue } from './label-value';

export type TrackPreviewProps = {
  track: TrackDefinition;
};

const formatFilterValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.map(formatFilterValue).join(', ');
  }
  if (value === null || value === undefined) {
    return 'No value';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value) ?? 'No value';
  }
  const serializedValue = JSON.stringify(value);
  return serializedValue ?? 'No value';
};

const formatFilterRule = (filter: CheckFilter): string =>
  Object.entries(filter)
    .map(([field, values]) => {
      const fieldLabel =
        TRACK_FILTER_FIELDS.find(option => option.value === field)?.label ??
        field;
      return `${fieldLabel}: ${formatFilterValue(values)}`;
    })
    .join('; ');

const formatFilters = (
  filters: CheckFilter[] | undefined,
  emptyLabel: string,
): string[] => {
  if (!filters?.length) {
    return [emptyLabel];
  }

  return filters.map(formatFilterRule);
};

export const TrackPreview = ({ track }: TrackPreviewProps) => {
  const status = track.inactive ? 'Inactive' : 'Active';
  const includeFilters = formatFilters(track.filter, 'all');
  const excludeFilters = formatFilters(track.exclude, 'none');
  const mandatoryChecks =
    track.levels.find(level => level.importance === 'mandatory')?.checkIds ??
    [];
  const recommendedChecks =
    track.levels.find(level => level.importance === 'recommended')?.checkIds ??
    [];
  const mandatoryLevelDescription = track.levels.find(
    level => level.importance === 'mandatory',
  )?.description;
  const recommendedLevelDescription = track.levels.find(
    level => level.importance === 'recommended',
  )?.description;

  return (
    <Card>
      <CardHeader>
        <Text variant="title-x-small">Preview</Text>
      </CardHeader>
      <CardBody>
        <Flex direction="column" gap="4">
          <Flex direction="column" gap="1">
            <Text variant="body-x-small" color="secondary">
              <code>{track.id || 'No Track ID'}</code>
            </Text>
            <Text variant="body-large" weight="bold">
              {track.name || 'Untitled Track'}
            </Text>
            <Text variant="body-small" color="secondary">
              {track.description || 'No description'}
            </Text>
          </Flex>

          <LabelValue
            label="Owner"
            value={
              track.ownerEntityRef ? (
                <EntityRefLink
                  entityRef={track.ownerEntityRef}
                  underline="none"
                />
              ) : (
                'No owner selected'
              )
            }
          />

          <Flex direction="column" gap="1">
            <Text variant="body-small" weight="bold">
              Scope
            </Text>
            <Text variant="body-small" color="secondary">
              Includes:{' '}
              {includeFilters.map((filter, index) => (
                <Text
                  variant="body-small"
                  color="secondary"
                  key={`${filter}-${index}`}
                >
                  {filter}
                </Text>
              ))}
            </Text>
            <Text variant="body-small" color="secondary">
              Excludes:{' '}
              {excludeFilters.map((filter, index) => (
                <Text
                  variant="body-small"
                  color="secondary"
                  key={`${filter}-${index}`}
                >
                  {filter}
                </Text>
              ))}
            </Text>
          </Flex>

          <LabelValue
            label="Documentation URL"
            value={track.documentationUrl || 'No documentation URL'}
          />
          <LabelValue label="Status" value={status} />

          <Flex direction="column" gap="1">
            <Text variant="body-small" weight="bold">
              Mandatory Checks
            </Text>
            {mandatoryLevelDescription && (
              <Text variant="body-small" color="secondary">
                {mandatoryLevelDescription}
              </Text>
            )}
            {mandatoryChecks.length > 0 ? (
              mandatoryChecks.map(checkId => (
                <Text variant="body-small" color="secondary" key={checkId}>
                  - {checkId}
                </Text>
              ))
            ) : (
              <Text variant="body-small" color="secondary">
                No mandatory checks
              </Text>
            )}
          </Flex>

          <Flex direction="column" gap="1">
            <Text variant="body-small" weight="bold">
              Recommended Checks
            </Text>
            {recommendedLevelDescription && (
              <Text variant="body-small" color="secondary">
                {recommendedLevelDescription}
              </Text>
            )}
            {recommendedChecks.length > 0 ? (
              recommendedChecks.map(checkId => (
                <Text variant="body-small" color="secondary" key={checkId}>
                  - {checkId}
                </Text>
              ))
            ) : (
              <Text variant="body-small" color="secondary">
                No recommended checks
              </Text>
            )}
          </Flex>
        </Flex>
      </CardBody>
    </Card>
  );
};
